import React, { useContext, useEffect, useMemo, useState } from "react"

import { gql, useMutation, useQuery } from "urql"

import { Color } from "~tailwindui/types/enums"

import TutorContext from "~Tutor/TutorContext"
import { Button, WithLoadingIndicator } from "~tailwindui/Basics"
import { buttonClassNames } from "~tailwindui/helpers/classNameHelpers"

import EventEmitter from "src/EventEmitter"
import classNames from "src/classNames"
import { RunMutation } from "src/types"

import useWebsocket from "hooks/useWebsocket"

import TimeProtectedLink from "components/TimeProtectedLink"
import TimeProtectedMiroLink from "components/TimeProtectedMiroLink"

const SessionResources = ({ id }) => {
  const { viewer } = useContext(TutorContext)
  const [googledocPending, setGoogledocPending] = useState(false)
  const [result, refetchQuery] = useQuery({
    query: resourcesQuery,
    variables: { id },
  })

  const channelProps = useMemo(() => {
    return { id }
  }, [id])

  useWebsocket({
    channelName: "AirtutorsSessionChannel",
    channelProps,
  })

  useEffect(() => {
    const onUpdate = () => {
      refetchQuery({ requestPolicy: "network-only" })
      setGoogledocPending(false)
    }
    EventEmitter.subscribe("update", onUpdate)
    return () => EventEmitter.unsubscribe("update", onUpdate)
  }, [refetchQuery])

  const [, runMutation]: [any, RunMutation] = useMutation(
    createGoogledocMutation
  )

  if (result.data && !result.data.airtutorsSession) return null

  return (
    <WithLoadingIndicator result={result}>
      {({ data: { airtutorsSession: session } }) => (
        <div className="flex flex-col gap-2 sm:flex-row">
          {session.realtimeboards[0] && (
            <TimeProtectedMiroLink
              tailwind
              startsAt={session.startsAt}
              email={viewer.ssoEmail}
              boardId={session.realtimeboards[0].id}
              buttonText={session.realtimeboards[0].displayName}
              boardPending={session.realtimeboardPending}
              buttonClassName={classNames(
                buttonClassNames({
                  color: Color.Green,
                })
              )}
            />
          )}

          {session.googledocUrl && (
            <TimeProtectedLink
              tailwind
              text="Google Doc"
              href={session.googledocUrl}
              startsAt={session.startsAt}
              className={classNames(
                buttonClassNames({
                  color: Color.Blue,
                })
              )}
            />
          )}

          {!session.googledocUrl &&
            (googledocPending ? (
              <Button disabled loading text="Google Doc Pending" />
            ) : (
              <Button
                onClick={() => {
                  runMutation({ id }).then(result => {
                    if (result.data.createGoogledoc.failures.length === 0)
                      setGoogledocPending(true)
                  })
                }}
              >
                Create Google Doc
              </Button>
            ))}
        </div>
      )}
    </WithLoadingIndicator>
  )
}

const resourcesQuery = gql`
  query SessionResources($id: ID!) {
    airtutorsSession(id: $id) {
      startsAt
      googledocUrl
      realtimeboardPending
      realtimeboards {
        id
        displayName
      }
    }
  }
`

const createGoogledocMutation = gql`
  mutation createGoogledoc($id: ID!) {
    createGoogledoc(id: $id) {
      failures {
        message
      }
    }
  }
`

export default SessionResources
